import { Check, ExpandMore } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import { CloseDialogIcon, MapPinIcon } from "assets/styles/icons";
import { useProgress } from "context/ProgressProvider";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { SecurityService } from "services/SecurityService";
import {
  AppFormikSelect,
  AppFormikTextField,
  AppPrimaryButton,
  AppSecondaryButton,
} from "shared/components/AppComponents";
import { PROPERTY_TYPE } from "shared/helpers/Constants";
import {
  EstimatedPropertyAmountSchema,
  ExistingLoanAmountSchema,
  IsAnyCaveatsSchema,
  IsAnyExistingLoanSchema,
  IsPropertyOwnedSchema,
  MortgageSchema,
  MultiPropertyAddressSchema,
  NoOfPropertiesSchema,
  PropertyAddressSchema,
  PropertyTypeSchema,
} from "shared/helpers/Schema";
import Utils from "shared/helpers/Utils";
import { ProfileActions } from "state/profile/Action";
import DialogFirstMortgage from "../verification/dialog/DialogFirstMortgage";
import RequestCallBackInitiated from "../verification/dialog/RequestCallBackInitiated";

const PropertyDetails = () => {
  const {
    handleCompleteAndNext,
    navigateToSectionStepHandler,
    currentProfile,
    isBroker,
  } = useProgress();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { step: urlStep } = useParams<{ step: string }>();
  const authInfo = useSelector((state: any) => state.authState);

  const currentStep = urlStep ? parseInt(urlStep, 10) : 1;

  const PAGE_LENGTH = 9;
  const PAGE_ID = "sec-property";

  const isValidStep = currentStep >= 1 && currentStep < PAGE_LENGTH;

  useEffect(() => {
    if (!isValidStep) {
      navigate(`/sbl/${PAGE_ID}/1`, { replace: true });
    }
    console.log(currentStep);
  }, [currentStep, isValidStep, navigate]);

  const [dialogCount, setDialogcount] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [properySecurityRequired, setProperySecurityRequired] = useState("");
  const [multiPropertiesData, setMultiPropertiesData] = useState([]);
  const [showRemoveCaveats, setShowRemoveCaveats] = useState(false);
  const [showRequestCallBackInitiated, setRequestCallBackInitiated] =
    useState(false);
  const currentPropertyIndex = multiPropertiesData.findIndex(
    (item) => item.status === "pending"
  );
  const currentProperty = multiPropertiesData[currentPropertyIndex];
  const nextStep = currentStep + 1;
  const jumpStep = currentStep + 2;

  let baseProperty = {
    propertyType: "",
    propertyAddress: "",
    estimatedPropertyAmount: null,
    isAnyExistingLoan: null,
    existingLoanSettlementOption: "",
    existingLoanAmount: null,
    isAnyCaveats: null,
    status: "pending",
  };

  const handleAddMultiProperty = (data, formik = null) => {
    console.log(data);
    SecurityService.addSecurity(
      {
        isPropertyOwned: true, //always true if reach this
        noOfProperties: currentProfile.noOfProperties,
        properties: data,
      },
      currentProfile.loadId
    )
      .then((response: any) => {
        dispatch(
          ProfileActions.setSblProfile({
            properties: data,
          })
        );
        handleCompleteAndNext();
      })
      .catch((error: any) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {});
  };

  const handleSubmitMultiForm = (
    value,
    index,
    isCompleted = false,
    formik = null
  ) => {
    console.log(
      parseFloat(String(value.estimatedPropertyAmount).replace(/,/g, ""))
    );

    if (
      currentStep === 5 &&
      value.existingLoanSettlementOption === "I’ll decide later"
    ) {
      setProperySecurityRequired("mortgage");
      return;
    }

    if (currentStep === 6) {
      if (value.isAnyCaveats) {
        setShowRemoveCaveats(true);
        return;
      }
    }

    const updated = [...multiPropertiesData];

    updated[index] = {
      ...updated[index],
      ...value,
      status: isCompleted ? "completed" : updated[index].status,
    };

    setMultiPropertiesData(updated);
    if (isCompleted) {
      if (index + 1 === multiPropertiesData.length) {
        setSubmitLoading(true);
        const formattedData = updated.map(({ status, ...rest }) => ({
          ...rest,
          estimatedPropertyAmount: rest.estimatedPropertyAmount
            ? parseFloat(String(rest.estimatedPropertyAmount).replace(/,/g, ""))
            : rest.estimatedPropertyAmount,
          existingLoanAmount: rest.existingLoanAmount
            ? parseFloat(String(rest.existingLoanAmount).replace(/,/g, ""))
            : rest.existingLoanAmount,
        }));

        handleAddMultiProperty(formattedData, formik);
      } else {
        navigateToSectionStepHandler(PAGE_ID, 3);
      }
      return;
    }
    if (currentStep === 4) {
      if (!value.isAnyExistingLoan) {
        navigateToSectionStepHandler(PAGE_ID, jumpStep);
        return;
      }
    }
    if (nextStep < PAGE_LENGTH) {
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    }
  };

  const handleSubmitForm = (value) => {
    console.log(value);

    if (currentStep === 1 && !value.isPropertyOwned) {
      if (!value.isPropertyOwned && currentProfile.isPropertyOwned !== false) {
        setProperySecurityRequired("owned");
        return;
      }
      if (currentProfile.isPropertyOwned === false) {
        setDialogcount((prev) => prev + 1);
        setProperySecurityRequired(`callback${dialogCount}`);
        console.log(2);
        return;
      }

      console.log(3);
    }
    dispatch(ProfileActions.setSblProfile(value));
    if (currentStep === 2) {
      const numberOfProperties = Number(value.noOfProperties);

      if (!isNaN(numberOfProperties) && numberOfProperties > 1) {
        const dataProperties = Array.from(
          { length: numberOfProperties },
          () => ({
            ...baseProperty,
          })
        );

        setMultiPropertiesData(dataProperties);
      }
    }
    if (currentStep === 6 && !value.isAnyExistingLoan) {
      handleCompleteAndNext();
      return;
    }
    if (
      currentStep === 7 &&
      value.existingLoanSettlementOption === "I’ll decide later"
    ) {
      setProperySecurityRequired("mortgage");
      return;
    }
    if (nextStep < PAGE_LENGTH) {
      navigateToSectionStepHandler(PAGE_ID, nextStep);
    } else {
      handleCompleteAndNext();
    }
  };

  const handleCheckExistingLoan = (errors: any, values: any): boolean => {
    if (!values.isAnyExistingLoan) {
      return false;
    }
    if (Object.keys(errors).length > 0) return true;
  };

  const handleCloseRemoveCaveats = () => {
    setShowRemoveCaveats(false);
    setSubmitLoading(false);
  };

  useEffect(() => {
    console.log(multiPropertiesData);
  }, [multiPropertiesData]);

  if (!isValidStep) {
    return null;
  }

  return (
    <>
      <DialogFirstMortgage
        properySecurityRequired={properySecurityRequired}
        setProperySecurityRequired={setProperySecurityRequired}
      />

      {currentStep === 1 && (
        <Box>
          <Formik
            enableReinitialize
            validationSchema={IsPropertyOwnedSchema}
            validateOnMount
            onSubmit={(value, formik) => handleSubmitForm(value)}
            initialValues={{
              isPropertyOwned: currentProfile.isPropertyOwned,
            }}
          >
            {({ values, setFieldValue, handleSubmit, errors }) => (
              <form autoComplete="off">
                <Typography variant="h2">Security</Typography>
                <Typography variant="h3" mt={3} maxWidth={"90%"}>
                  Please confirm that at least one of the applicants holds a
                  legal or beneficial interest in the property being used as
                  security?
                </Typography>
                <Box mt={2}>
                  <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                    {["Yes", "No"].map((option) => {
                      const isYes = option === "Yes";
                      return (
                        <Chip
                          key={option}
                          label={option}
                          clickable
                          variant="outlined"
                          color={
                            values.isPropertyOwned === isYes
                              ? "primary"
                              : "default"
                          }
                          icon={
                            values.isPropertyOwned === isYes ? (
                              <Check fontSize="small" />
                            ) : (
                              <></>
                            )
                          }
                          onClick={() => {
                            setFieldValue("isPropertyOwned", isYes);
                          }}
                        />
                      );
                    })}
                  </Box>
                </Box>

                <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                  <AppPrimaryButton
                    title="Continue"
                    isLoading={false}
                    loadingText="Please wait.."
                    onClick={handleSubmit}
                    disabled={Object.keys(errors).length > 0}
                  />
                </Box>
              </form>
            )}
          </Formik>
        </Box>
      )}

      {currentStep === 2 && (
        <>
          {currentProfile.properties && currentProfile.properties.length > 1 ? (
            <>
              <Typography variant="h2">Security</Typography>
              <Typography variant="h3" mt={3}>
                Please provide details for each property offered as security:
              </Typography>
              <Box mt={2}>
                {currentProfile.properties.map((data, index) => {
                  return (
                    <Box mt={3}>
                      <Typography>
                        Property {index + 1}{" "}
                        {`(${index + 1}/${currentProfile.properties.length})`}
                      </Typography>
                      <Box
                        sx={{
                          width: "485px",
                          borderRadius: "8px",
                          border: "1px solid #E5E5E5",
                          padding: "12px 16px",
                          display: "flex",
                          gap: "12px",
                          alignItems: "center",
                          cursor: "pointer",
                        }}
                        mt={2}
                      >
                        <Avatar
                          alt={data.propertyAddress}
                          src="/images/avatar.png"
                        />
                        <Box sx={{ width: "70%" }}>
                          <Typography
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {data.propertyAddress}
                          </Typography>
                          <Typography fontWeight={200}>
                            {data.propertyType}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            gap: "10px",
                            alignItems: "center",
                          }}
                        >
                          <Typography color="#7DDB25">Completed</Typography>
                          {/* <img src="/images/arrow-right.png" height={12} /> */}
                        </Box>
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                <AppPrimaryButton
                  title="Continue"
                  isLoading={false}
                  onClick={() => handleCompleteAndNext()}
                />
              </Box>
            </>
          ) : (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={NoOfPropertiesSchema}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  noOfProperties: currentProfile.noOfProperties,
                }}
              >
                {({ handleSubmit, errors }) => (
                  <form autoComplete="off">
                    <Typography variant="h2">Security</Typography>
                    <Typography variant="h3" mt={3}>
                      {isBroker
                        ? "How many properties are they offering?"
                        : "How many properties are you offering?"}
                    </Typography>
                    <AppFormikTextField
                      label=""
                      name="noOfProperties"
                      outerLabel
                      className="ezifin-default-field"
                      type="number"
                    />

                    <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                      <AppPrimaryButton
                        title="Continue"
                        isLoading={false}
                        loadingText="Please wait.."
                        onClick={handleSubmit}
                        disabled={Object.keys(errors).length > 0}
                      />
                    </Box>
                  </form>
                )}
              </Formik>
            </Box>
          )}
        </>
      )}

      {/* -------- SINGLE PROPERTIES -------- */}
      {Number(currentProfile.noOfProperties) === 1 && (
        <>
          {currentStep === 3 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={PropertyTypeSchema}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  propertyType: currentProfile.propertyType,
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  const typePropertyOptions = [
                    "Residential",
                    "Commercial",
                    "Vacant Land",
                    "Mixed use",
                  ];
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        Please select the type property that will be used as
                        security for this loan.
                      </Typography>
                      {/* CHIPS */}
                      <Box mt={2}>
                        {typePropertyOptions.map((option) => (
                          <Box mt={2} key={option}>
                            <Chip
                              label={option}
                              variant="outlined"
                              color={
                                values.propertyType === option
                                  ? "primary"
                                  : "default"
                              }
                              icon={
                                values.propertyType === option ? (
                                  <Check fontSize="small" />
                                ) : (
                                  <></>
                                )
                              }
                              onClick={() =>
                                setFieldValue("propertyType", option)
                              }
                              clickable
                            />
                          </Box>
                        ))}
                      </Box>

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Continue"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={Object.keys(errors).length > 0}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}

          {currentStep === 4 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={PropertyAddressSchema}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  propertyAddress: currentProfile.propertyAddress,
                }}
              >
                {({ handleSubmit, errors }) => (
                  <form autoComplete="off">
                    <Typography variant="h2">Security</Typography>
                    <Typography variant="h3" mt={3}>
                      What is the property address?
                    </Typography>
                    <AppFormikTextField
                      placeholder="Address"
                      label=""
                      name="propertyAddress"
                      outerLabel
                      className="ezifin-default-field"
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              <MapPinIcon />
                            </InputAdornment>
                          ),
                        },
                      }}
                      allowComma={true}
                    />

                    <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                      <AppPrimaryButton
                        title="Continue"
                        isLoading={false}
                        loadingText="Please wait.."
                        onClick={handleSubmit}
                        disabled={Object.keys(errors).length > 0}
                      />
                    </Box>
                  </form>
                )}
              </Formik>
            </Box>
          )}

          {currentStep === 5 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={EstimatedPropertyAmountSchema}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  estimatedPropertyAmount: Utils.formatCurrency(
                    currentProfile.estimatedPropertyAmount
                  ),
                }}
              >
                {({ handleSubmit, errors }) => (
                  <form autoComplete="off">
                    <Typography variant="h2">Security</Typography>
                    <Typography variant="h3" mt={3}>
                      What is the estimated property value?
                    </Typography>
                    <AppFormikTextField
                      placeholder="Enter amount"
                      label=""
                      name="estimatedPropertyAmount"
                      outerLabel
                      className="ezifin-default-field"
                      slotProps={{
                        input: {
                          startAdornment: (
                            <InputAdornment position="start">$</InputAdornment>
                          ),
                        },
                      }}
                      disabled={Object.keys(errors).length > 0}
                      customIsoDollarDecimal
                    />

                    <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                      <AppPrimaryButton
                        title="Continue"
                        isLoading={false}
                        loadingText="Please wait.."
                        onClick={handleSubmit}
                      />
                    </Box>
                  </form>
                )}
              </Formik>
            </Box>
          )}

          {currentStep === 6 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={IsAnyExistingLoanSchema}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  isAnyExistingLoan: currentProfile.isAnyExistingLoan,
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  console.log(errors);
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        Is the property presently offered as security for an
                        existing loan?
                      </Typography>
                      <Box mt={2}>
                        <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                          {["Yes", "No"].map((option) => {
                            const isYes = option === "Yes";
                            return (
                              <Chip
                                key={option}
                                label={option}
                                clickable
                                variant="outlined"
                                color={
                                  values.isAnyExistingLoan === isYes
                                    ? "primary"
                                    : "default"
                                }
                                icon={
                                  values.isAnyExistingLoan === isYes ? (
                                    <Check fontSize="small" />
                                  ) : (
                                    <></>
                                  )
                                }
                                onClick={() =>
                                  setFieldValue("isAnyExistingLoan", isYes)
                                }
                              />
                            );
                          })}
                        </Box>
                      </Box>

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Continue"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={Object.keys(errors).length > 0}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}

          {currentStep === 7 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={MortgageSchema}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  existingLoanSettlementOption:
                    currentProfile.existingLoanSettlementOption,
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  const typeMorgageOptions = [
                    "I want to refinance",
                    "I will payout",
                    "I’ll decide later",
                  ];
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        As we require first mortgage on the property/security
                        this loan will need to be cleared prior to settlement or
                        refinanced to ezifin.
                      </Typography>
                      {/* CHIPS */}
                      <Box mt={2}>
                        {typeMorgageOptions.map((option) => (
                          <Box mt={2} key={option}>
                            <Chip
                              label={option}
                              variant="outlined"
                              color={
                                values.existingLoanSettlementOption === option
                                  ? "primary"
                                  : "default"
                              }
                              icon={
                                values.existingLoanSettlementOption ===
                                option ? (
                                  <Check fontSize="small" />
                                ) : (
                                  <></>
                                )
                              }
                              onClick={() =>
                                setFieldValue(
                                  "existingLoanSettlementOption",
                                  option
                                )
                              }
                              clickable
                            />
                          </Box>
                        ))}
                      </Box>

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Continue"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={Object.keys(errors).length > 0}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}

          {currentStep === 8 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={ExistingLoanAmountSchema(
                  currentProfile.estimatedPropertyAmount
                )}
                validateOnMount
                onSubmit={(value, formik) => handleSubmitForm(value)}
                initialValues={{
                  isAnyExistingLoan: currentProfile.isAnyExistingLoan,
                  existingLoanAmount: Utils.formatCurrency(
                    currentProfile.existingLoanAmount
                  ),
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  console.log(errors);
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        Is the property presently offered as security for an
                        existing loan?
                      </Typography>
                      <Box mt={2}>
                        <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                          {["Yes", "No"].map((option) => {
                            const isYes = option === "Yes";
                            return (
                              <Chip
                                key={option}
                                label={option}
                                clickable
                                variant="outlined"
                                color={
                                  values.isAnyExistingLoan === isYes
                                    ? "primary"
                                    : "default"
                                }
                                icon={
                                  values.isAnyExistingLoan === isYes ? (
                                    <Check fontSize="small" />
                                  ) : (
                                    <></>
                                  )
                                }
                                onClick={() => {
                                  setFieldValue("isAnyExistingLoan", isYes);
                                  if (!isYes) {
                                    setFieldValue("existingLoanAmount", 0);
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>

                      {values.isAnyExistingLoan && (
                        <Box mt={3}>
                          <Typography variant="h3" mt={3}>
                            If yes, what is the total amount of this
                            loan/guarantee?
                          </Typography>
                          <AppFormikTextField
                            placeholder="Enter amount"
                            label=""
                            name="existingLoanAmount"
                            outerLabel
                            className="ezifin-default-field"
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              },
                            }}
                            customIsoDollarDecimal
                          />
                        </Box>
                      )}

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Continue"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={handleCheckExistingLoan(errors, values)}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}
        </>
      )}

      {/* -------- MULTI PROPERTIES -------- */}
      {Number(currentProfile.noOfProperties) > 1 && (
        <>
          {currentProperty && currentStep === 3 && (
            <Box mt={4}>
              <Formik
                enableReinitialize
                validationSchema={MultiPropertyAddressSchema}
                validateOnMount
                onSubmit={(value) =>
                  handleSubmitMultiForm(value, currentPropertyIndex)
                }
                initialValues={{
                  propertyAddress: currentProperty.propertyAddress || "",
                  propertyType: currentProperty.propertyType || "",
                  estimatedPropertyAmount:
                    Utils.formatCurrency(
                      currentProperty.estimatedPropertyAmount
                    ) || null,
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        Please provide details for each property offered as
                        security:
                      </Typography>
                      <Typography variant="h3" mt={3} fontWeight={400}>
                        Property {currentPropertyIndex + 1} (
                        {currentPropertyIndex + 1}/{multiPropertiesData.length})
                      </Typography>

                      <Box mt={2}>
                        <AppFormikTextField
                          placeholder="Address"
                          label="Address"
                          name="propertyAddress"
                          outerLabel
                          className="ezifin-default-field"
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <MapPinIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                          allowComma={true}
                        />
                      </Box>

                      <Box mt={2}>
                        <AppFormikSelect
                          placeholder="Select"
                          label="Type"
                          name="propertyType"
                          items={PROPERTY_TYPE}
                          valueKey="value"
                          titleKey="label"
                          outerLabel
                          iconComponent={ExpandMore}
                          className="ezifin-default-field"
                        />
                      </Box>

                      <Box mt={2}>
                        <AppFormikTextField
                          placeholder="Value"
                          label="Value"
                          name="estimatedPropertyAmount"
                          outerLabel
                          className="ezifin-default-field"
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  $
                                </InputAdornment>
                              ),
                            },
                          }}
                          allowComma={true}
                          customIsoDollarDecimal
                        />
                      </Box>

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Add"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={Object.keys(errors).length > 0}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}

          {currentProperty && currentStep === 4 && (
            <Box mt={4}>
              <Formik
                enableReinitialize
                validationSchema={ExistingLoanAmountSchema(
                  currentProperty.estimatedPropertyAmount
                )}
                validateOnMount
                onSubmit={(value, formik) =>
                  handleSubmitMultiForm(value, currentPropertyIndex)
                }
                initialValues={{
                  isAnyExistingLoan: currentProperty.isAnyExistingLoan,
                  existingLoanAmount: Utils.formatCurrency(
                    currentProperty.existingLoanAmount
                  ),
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  console.log(errors);
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        Is Property {currentPropertyIndex + 1}{" "}
                        {`(${currentProperty?.propertyAddress})` || ""}{" "}
                        currently offered as security for an existing loan?
                      </Typography>
                      <Box mt={2}>
                        <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                          {["Yes", "No"].map((option) => {
                            const isYes = option === "Yes";
                            return (
                              <Chip
                                key={option}
                                label={option}
                                clickable
                                variant="outlined"
                                color={
                                  values.isAnyExistingLoan === isYes
                                    ? "primary"
                                    : "default"
                                }
                                icon={
                                  values.isAnyExistingLoan === isYes ? (
                                    <Check fontSize="small" />
                                  ) : (
                                    <></>
                                  )
                                }
                                onClick={() => {
                                  setFieldValue("isAnyExistingLoan", isYes);
                                  if (!isYes) {
                                    setFieldValue("existingLoanAmount", 0);
                                  }
                                }}
                              />
                            );
                          })}
                        </Box>
                      </Box>

                      {values.isAnyExistingLoan && (
                        <Box mt={3}>
                          <Typography variant="h3" mt={3}>
                            If yes, what is the total amount of this
                            loan/guarantee?
                          </Typography>
                          <AppFormikTextField
                            placeholder="Enter amount"
                            label=""
                            name="existingLoanAmount"
                            outerLabel
                            className="ezifin-default-field"
                            slotProps={{
                              input: {
                                startAdornment: (
                                  <InputAdornment position="start">
                                    $
                                  </InputAdornment>
                                ),
                              },
                            }}
                            customIsoDollarDecimal
                          />
                        </Box>
                      )}

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Continue"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={handleCheckExistingLoan(errors, values)}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}

          {currentProperty && currentStep === 5 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={MortgageSchema}
                validateOnMount
                onSubmit={(value, formik) =>
                  handleSubmitMultiForm(value, currentPropertyIndex)
                }
                initialValues={{
                  existingLoanSettlementOption:
                    currentProperty.existingLoanSettlementOption,
                }}
              >
                {({ values, setFieldValue, handleSubmit, errors }) => {
                  const typeMorgageOptions = [
                    "I want to refinance",
                    "I will payout",
                    "I’ll decide later",
                  ];
                  return (
                    <form autoComplete="off">
                      <Typography variant="h2">Security</Typography>
                      <Typography variant="h3" mt={3}>
                        As we require first mortgage on the property/security
                        this loan will need to be cleared
                        <br /> prior to settlement or refinanced to ezifin.
                      </Typography>
                      {/* CHIPS */}
                      <Box mt={2}>
                        {typeMorgageOptions.map((option) => (
                          <Box mt={2} key={option}>
                            <Chip
                              label={option}
                              variant="outlined"
                              color={
                                values.existingLoanSettlementOption === option
                                  ? "primary"
                                  : "default"
                              }
                              icon={
                                values.existingLoanSettlementOption ===
                                option ? (
                                  <Check fontSize="small" />
                                ) : (
                                  <></>
                                )
                              }
                              onClick={() =>
                                setFieldValue(
                                  "existingLoanSettlementOption",
                                  option
                                )
                              }
                              clickable
                            />
                          </Box>
                        ))}
                      </Box>

                      <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                        <AppPrimaryButton
                          title="Continue"
                          isLoading={false}
                          loadingText="Please wait.."
                          onClick={handleSubmit}
                          disabled={Object.keys(errors).length > 0}
                        />
                      </Box>
                    </form>
                  );
                }}
              </Formik>
            </Box>
          )}

          {currentProperty && currentStep === 6 && (
            <Box>
              <Formik
                enableReinitialize
                validationSchema={IsAnyCaveatsSchema}
                validateOnMount
                onSubmit={(value, formik) =>
                  handleSubmitMultiForm(
                    value,
                    currentPropertyIndex,
                    true,
                    formik
                  )
                }
                initialValues={{
                  isAnyCaveats: currentProperty.isAnyCaveats,
                }}
              >
                {({
                  values,
                  setFieldValue,
                  handleSubmit,
                  errors,
                  isSubmitting,
                }) => (
                  <form autoComplete="off">
                    <Typography variant="h2">Security</Typography>
                    <Typography variant="h3" mt={3}>
                      Are there any caveats on the property?
                    </Typography>
                    <Box mt={2}>
                      <Box mt={2} sx={{ display: "flex", gap: "12px" }}>
                        {["Yes", "No"].map((option) => (
                          <Chip
                            key={option}
                            label={option}
                            clickable
                            variant="outlined"
                            color={
                              values.isAnyCaveats === (option === "Yes")
                                ? "primary"
                                : "default"
                            }
                            icon={
                              values.isAnyCaveats === (option === "Yes") ? (
                                <Check fontSize="small" />
                              ) : (
                                <></>
                              )
                            }
                            onClick={() => {
                              setFieldValue("isAnyCaveats", option === "Yes");
                            }}
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box mt={4} sx={{ display: "flex", gap: "12px" }}>
                      <AppPrimaryButton
                        title="Continue"
                        isLoading={submitLoading}
                        loadingText="Please wait.."
                        onClick={handleSubmit}
                        disabled={
                          Object.keys(errors).length > 0 || submitLoading
                        }
                      />
                    </Box>
                  </form>
                )}
              </Formik>
            </Box>
          )}

          <Dialog open={showRemoveCaveats} fullWidth maxWidth="sm">
            <DialogTitle>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography variant="h2">Remove caveats</Typography>
                <IconButton onClick={() => handleCloseRemoveCaveats()}>
                  <CloseDialogIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent>
              <Typography variant="h3" fontWeight={400} mb={3}>
                For you to proceed with the application, you’ll need to remove
                the caveat/caveats from the property.
              </Typography>
            </DialogContent>

            <DialogActions sx={{ gap: "12px" }}>
              <AppSecondaryButton
                title="Cancel"
                className="btn-small ezifin-default-outlined-button"
                onClick={() => handleCloseRemoveCaveats()}
                fullWidth
              />
              <AppPrimaryButton
                title="Okay, I understand"
                className="btn-small"
                onClick={() => {
                  handleCloseRemoveCaveats();
                }}
                fullWidth
              />
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* {showRequestCallBackInitiated && (
        <RequestCallBackInitiated
          showRequest={showRequestCallBackInitiated}
          onCloseRequest={() => setRequestCallBackInitiated(false)}
        />
      )} */}
    </>
  );
};

export default PropertyDetails;
